import { ROLES } from "@/constants/app.constant";
import { LOGIN_SCOPES, msalApp } from "../constants/auth.constant";
import {InteractionRequiredAuthError , BrowserAuthError} from "@azure/msal-browser";

/**
 * useAuthService hook provides authentication-related service functions.
 * Handles Microsoft MSAL authentication, token acquisition, user roles, and logout.
 * 
 * @hook
 * @returns {Object} Auth service methods
 * @returns {Function} returns.getUserAccessToken - Get user access token (silent or popup)
 * @returns {Function} returns.getRolesFromToken - Get user roles from token
 * @returns {Function} returns.getCurrentUserIsAdmin - Check if current user is admin
 * @returns {Function} returns.getActiveUserDetails - Get active user account details
 * @returns {Function} returns.getAvatar - Get user avatar initials
 * @returns {Function} returns.logout - Logout user and clear session
 * 
 * @example
 * ```tsx
 * const { getUserAccessToken, getCurrentUserIsAdmin } = useAuthService();
 * 
 * const token = await getUserAccessToken();
 * const isAdmin = getCurrentUserIsAdmin();
 * ```
 */
const useAuthService = () => {

    /**
     * Gets user access token, attempting silent acquisition first, then popup if needed.
     * Handles authentication errors and token refresh.
     * 
     * @returns {Promise<string | null>} Access token or null if unavailable
     * @throws {Error} If token acquisition fails after all attempts
     */
    const getUserAccessToken = async () => {
        if (!msalApp) return null;

        const activeAccount = msalApp.getActiveAccount();
        const accounts = msalApp.getAllAccounts();
        if (!activeAccount && accounts.length === 0) return null;

        const request = {
            scopes: LOGIN_SCOPES,
            account: activeAccount || accounts[0],
        };

        try {
            const authResult = await msalApp.acquireTokenSilent(request);
            return authResult.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
                try {
                    const authResult = await msalApp.acquireTokenPopup(request);
                    return authResult.accessToken;
                } catch (popupError) {
                    await msalApp['browserStorage'].clear()
                    window.location.reload();
                    // await logout();
                    return null;
                }
            } else {
                console.error('Token acquisition error:', error);
                return null;
            }
        }
    };
    
    /**
     * Gets user roles from the current account's ID token claims.
     * 
     * @returns {any | null} Array of user roles or null if no account
     */
    const getRolesFromToken = (): any | null => {
        
        const activeAccount = msalApp.getActiveAccount();
        const accounts = msalApp.getAllAccounts();

        if (!activeAccount && accounts.length === 0) return null;


        const currentAccount = activeAccount || accounts[0];

        return currentAccount.idTokenClaims?.roles;

    };

    /**
     * Checks if the current user has admin role.
     * 
     * @returns {boolean | null} True if admin, false if not, null if no account
     */
    const getCurrentUserIsAdmin = (): boolean | null => {
        const activeAccount = msalApp.getActiveAccount();
        const accounts = msalApp.getAllAccounts();

        if (!activeAccount && accounts.length === 0) return null;


        const currentAccount = activeAccount || accounts[0];
        
        if (currentAccount.idTokenClaims?.roles?.includes(ROLES.ADMIN)) {
            return true
        } else return false;
    }
    
    /**
     * Gets active user account details from MSAL.
     * 
     * @returns {AccountInfo | null} Active account info or null if no account
     */
    const getActiveUserDetails = () => {
        if (!msalApp) return null;

        const activeAccount = msalApp.getActiveAccount();
        const accounts = msalApp.getAllAccounts();

        if (!activeAccount && accounts.length === 0) return null;

        const currentAccount = activeAccount || accounts[0];

        return currentAccount
    }

    /** Gets user's group details from MSAL */
    const getUserGroups = () => {
        if (!msalApp) return null;
        const activeAccount = msalApp.getActiveAccount();
        const accounts = msalApp.getAllAccounts();
        if (!activeAccount && accounts.length === 0) return null;
        const currentAccount = activeAccount || accounts[0];
        return currentAccount.idTokenClaims?.groups || null;
    }

    /**
     * Generates user avatar initials from account name.
     * Handles comma-separated names (Last, First) and regular names.
     * 
     * @returns {string | null} Two-letter initials or null if no account
     */
    const getAvatar = () => {
        if (!msalApp) return null;
        const activeAccount = msalApp.getActiveAccount();
        const accounts = msalApp.getAllAccounts();
        if (!activeAccount && accounts.length === 0) return null;
        const currentAccount = activeAccount || accounts[0];
        const name = currentAccount?.name || currentAccount?.username;
        const splitName = name.split(', ');
        if (splitName.length > 0) {
            return `${splitName[0][0]}${splitName[1][0]}`;
        } else {
            return `${name[0]}${name[1]}`;
        }
    }

    /**
     * Logs out the current user, clears MSAL cache, and reloads the page.
     * Dispatches 'logout-start' event before logout.
     * 
     * @returns {Promise<void>} Promise that resolves when logout completes
     * @throws {Error} If MSAL client is invalid
     */
    const logout = () => {
        return new Promise((resolve, reject) => {
            if (!msalApp) reject('Invalid msal client');

            window.dispatchEvent(new CustomEvent('logout-start'));

            msalApp.logoutRedirect({
                postLogoutRedirectUri: '/'
            })
            .then(() => {
                window.dispatchEvent(new CustomEvent('logout-end'));
                resolve('User logged out successfully');
            })
            .catch((error) => {
                window.dispatchEvent(new CustomEvent('logout-end'));
                reject(error);
            });
        });
    }

    return {
        getUserAccessToken,
        getActiveUserDetails,
        logout,
        getRolesFromToken,
        getCurrentUserIsAdmin,
        getAvatar,
        getUserGroups
    }
}

export default useAuthService;